package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.Switch
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.theme.ThemeSegmented
import co.maytes.standupbot.dashboard.styles.FocusRing
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

const val DENSITY_COMPACT_CLASS = "sb-density-compact"
private const val ROUTE = "/ds"

fun isDesignSystemRoute(): Boolean {
    val path = window.location.pathname
    return path == ROUTE || path == "$ROUTE/" || path.startsWith("$ROUTE/")
}
private const val PAGE_TITLE = "Design system - Standup Bot (unlisted)"

private enum class Density { Comfortable, Compact }

private class SectionLink(val id: String, val label: String)

private val sectionLinks = listOf(
    SectionLink("colour", "Colour"),
    SectionLink("typography", "Typography"),
    SectionLink("space", "Space"),
    SectionLink("radius", "Radius"),
    SectionLink("elevation", "Elevation"),
    SectionLink("z-index", "Z-index"),
    SectionLink("layout", "Layout"),
    SectionLink("motion", "Motion"),
    SectionLink("actions", "Actions"),
    SectionLink("fields", "Form controls"),
    SectionLink("components", "Components"),
)

@Composable
fun DesignSystemGallery() {
    val scheme = rememberGalleryScheme()
    var density by remember { mutableStateOf(Density.Comfortable) }
    var reducedMotion by remember { mutableStateOf(false) }

    UnlistedPage()
    DocumentClass(Motion.FORCE_REDUCED_MOTION_CLASS, reducedMotion)
    DocumentClass(DENSITY_COMPACT_CLASS, density == Density.Compact)
    DisposableEffect(Unit) {
        ForcedStates.install()
        onDispose { }
    }

    Div(
        attrs = {
            style {
                property("width", "100%")
                property("min-height", "100vh")
                property("background-color", Palette.background.cssRgb())
                property("color", Palette.textPrimary.cssRgb())
            }
        },
    ) {
        GalleryHeader(
            density = density,
            onDensity = { density = it },
            reducedMotion = reducedMotion,
            onReducedMotion = { reducedMotion = it },
        )
        Div(
            attrs = {
                style {
                    property("max-width", "${Layout.shell}")
                    property("margin", "0 auto")
                    property("padding", "24px 16px 96px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "40px")
                }
            },
        ) {
            ColourSection(scheme)
            TypographySection()
            SpaceSection(compact = density == Density.Compact)
            RadiusSection()
            ElevationSection(scheme)
            ZIndexSection()
            LayoutSection()
            MotionSection()
            ActionsSection()
            FieldsSection()
            ComponentsSection()
        }
    }
}

@Composable
private fun GalleryHeader(
    density: Density,
    onDensity: (Density) -> Unit,
    reducedMotion: Boolean,
    onReducedMotion: (Boolean) -> Unit,
) {
    Div(
        attrs = {
            style {
                property("background-color", Palette.surface.cssRgb())
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("max-width", "${Layout.shell}")
                    property("margin", "0 auto")
                    property("padding", "12px 16px")
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("align-items", "center")
                    property("gap", "12px 24px")
                }
            },
        ) {
            Stack(2) {
                SpanText(
                    text = "Design system",
                    modifier = Modifier.styleModifier {
                        property("font-size", "${Typography.h4.size}")
                        property("font-weight", "${Typography.h4.weight}")
                    },
                )
                GalleryMono("Unlisted reference. Tokens and components as they are today.")
            }
            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("align-items", "center")
                        property("gap", "12px")
                        property("margin-left", "auto")
                    }
                },
            ) {
                ThemeSegmented()
                SegmentedControl(
                    options = Density.entries.map { SegmentedOption(it, it.name) },
                    selected = density,
                    onSelect = onDensity,
                    ariaLabel = "Density",
                )
                Switch(
                    checked = reducedMotion,
                    onCheckedChange = onReducedMotion,
                    label = "Reduced motion",
                )
            }
        }
    }
    SectionNav()
}

@Composable
private fun SectionNav() {
    Div(
        attrs = {
            attr("role", "navigation")
            attr("aria-label", "Sections")
            style {
                property("position", "sticky")
                property("top", "0")
                property("z-index", "${ZIndex.sticky}")
                property("background-color", Palette.surface.cssRgb())
                property("border-bottom", "1px solid ${Palette.border.cssRgb()}")
                property("padding", "10px max(16px, calc((100% - ${Layout.shell}) / 2 + 16px))")
                property("display", "flex")
                property("gap", "8px")
                property("overflow-x", "auto")
            }
        },
    ) {
        sectionLinks.forEach { section ->
            Div(
                attrs = Modifier
                    .classNames(FocusRing.VISIBLE_CLASS)
                    .role("link")
                    .tabIndex(0)
                    .onClick { scrollToSection(section.id) }
                    .onKeyDown { event ->
                        if (event.key == "Enter" || event.key == " ") {
                            event.preventDefault()
                            scrollToSection(section.id)
                        }
                    }
                    .styleModifier {
                        property("padding", "4px 10px")
                        property("border-radius", "9999px")
                        property("border", "1px solid ${Palette.border.cssRgb()}")
                        property("font-size", "${Typography.caption.size}")
                        property("color", Palette.textSecondary.cssRgb())
                        property("cursor", "pointer")
                        property("white-space", "nowrap")
                    }
                    .toAttrs(),
            ) {
                Text(section.label)
            }
        }
    }
}

private fun scrollToSection(id: String) {
    document.getElementById(id)?.scrollIntoView()
}

@Composable
private fun DocumentClass(name: String, enabled: Boolean) {
    DisposableEffect(name, enabled) {
        val root = document.documentElement
        if (enabled) root?.classList?.add(name)
        onDispose { root?.classList?.remove(name) }
    }
}

@Composable
private fun UnlistedPage() {
    DisposableEffect(Unit) {
        val previousTitle = document.title
        document.title = PAGE_TITLE
        val robots = document.createElement("meta")
        robots.setAttribute("name", "robots")
        robots.setAttribute("content", "noindex, nofollow")
        document.head?.appendChild(robots)
        onDispose {
            document.title = previousTitle
            robots.remove()
        }
    }
}
